import { useMemo, useState } from 'react';
import { ChevronRight, UserPlus } from 'lucide-react';

export const ROLE_LABELS = {
  GERANT: 'Gérante',
  MANAGER: 'Manager',
  VENDEUR: 'Vendeur',
  CAISSIER: 'Caissier',
};

/** Libellé neutre — capacité opérationnelle (pas un rôle permission). */
export const PRESTATION_CAPABILITY_LABEL = 'Réalise des prestations';
export const NO_PRESTATION_CAPABILITY_LABEL = 'Ne réalise pas de prestations';

export const JOURS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 0, label: 'Dimanche' },
];

export function getMemberInitials(member) {
  const p = (member?.prenom || '').trim().charAt(0);
  const n = (member?.nom || '').trim().charAt(0);
  return (p + n).toUpperCase() || '?';
}

export function getMemberRoleLabel(member) {
  if (member?.protections?.isGerant || member?.roleInterne === 'GERANT') {
    return ROLE_LABELS.GERANT;
  }
  if (member?.hasAdminAccess && member?.roleInterne) {
    return ROLE_LABELS[member.roleInterne] || 'Accès Clickorico';
  }
  if (member?.isIntervenant) return PRESTATION_CAPABILITY_LABEL;
  return 'Membre de l\'équipe';
}

export function buildMemberBadges(member) {
  const badges = [];
  if (member?.protections?.isGerant || member?.roleInterne === 'GERANT') {
    badges.push({ key: 'gerant', label: 'Gérante', className: 'bg-violet-100 text-violet-800' });
  } else if (member?.hasAdminAccess && member?.roleInterne) {
    badges.push({
      key: 'admin-role',
      label: ROLE_LABELS[member.roleInterne] || 'Accès Admin',
      className: 'bg-blue-100 text-blue-800',
    });
  }
  if (member?.isIntervenant) {
    badges.push({
      key: 'prestations',
      label: PRESTATION_CAPABILITY_LABEL,
      className: 'bg-emerald-100 text-emerald-800',
    });
  }
  // member.actif = Instructeur.actif (prestataire) ou User.statut (compte seul) — contrat API LOT1.
  if (member?.actif === false) {
    badges.push({ key: 'inactif', label: 'Inactif', className: 'bg-rose-100 text-rose-800' });
  } else if (member?.actif !== false) {
    badges.push({ key: 'actif', label: 'Actif', className: 'bg-green-50 text-green-700' });
  }
  return badges;
}

export function MemberAvatar({ member, size = 'md', className = '' }) {
  const sizes = {
    sm: 'h-10 w-10 text-sm',
    md: 'h-12 w-12 text-sm',
    lg: 'h-16 w-16 text-lg',
    xl: 'h-20 w-20 text-xl',
  };
  const photo = member?.photo?.trim();
  const sizeClass = sizes[size] || sizes.md;

  if (photo) {
    return (
      <img
        src={photo}
        alt=""
        className={`shrink-0 rounded-full object-cover shadow-sm ring-2 ring-white ${sizeClass} ${className}`}
      />
    );
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-violet-700 font-semibold text-white shadow-sm ${sizeClass} ${className}`}
      aria-hidden
    >
      {getMemberInitials(member)}
    </div>
  );
}

export function MemberBadge({ label, className = 'bg-gray-100 text-gray-700' }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

export function MemberHeader({ member }) {
  const badges = buildMemberBadges(member);
  const fonction = member?.fonctionMetier?.trim();

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="bg-gradient-to-r from-slate-50 to-white px-4 py-5 sm:px-6">
        <div className="flex items-start gap-4">
          <MemberAvatar member={member} size="lg" />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
              {member.prenom} {member.nom}
            </h1>
            {fonction && (
              <p className="mt-0.5 text-sm font-medium text-gray-700">{fonction}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {badges.map((b) => (
                <MemberBadge key={b.key} label={b.label} className={b.className} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MemberTabs({ tabs, activeTab, onChange }) {
  return (
    <div className="-mx-1 overflow-x-auto pb-1">
      <div className="flex min-w-max gap-1 rounded-xl bg-gray-100 p-1 sm:min-w-0 sm:flex-wrap">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition ${
                isActive
                  ? 'bg-white text-primary-700 shadow-sm ring-1 ring-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SectionCard({ title, description, children, className = '' }) {
  return (
    <section
      className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 ${className}`.trim()}
    >
      {title && (
        <div className="mb-4 border-b border-gray-100 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

export function NonIntervenantBlock({ onActivate }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
      <p className="text-sm text-gray-600">
        Cette personne ne réalise actuellement aucune prestation.
      </p>
      {onActivate && (
        <button
          type="button"
          onClick={onActivate}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <UserPlus className="h-4 w-4" />
          Activer « {PRESTATION_CAPABILITY_LABEL} »
        </button>
      )}
    </div>
  );
}

function groupPrestations(prestations, selectedIds) {
  const idSet = new Set(selectedIds || []);
  const selected = prestations.filter((p) => idSet.has(p.id));
  const groups = {};
  for (const p of selected) {
    const key = p.categorie?.trim() || 'Autres';
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  return { selected, groups, total: selected.length };
}

const PREVIEW_CHIP_LIMIT = 12;

export function PrestationsSummary({ prestations, selectedIds, onManage }) {
  const { groups, total } = useMemo(
    () => groupPrestations(prestations, selectedIds),
    [prestations, selectedIds],
  );
  const [expanded, setExpanded] = useState(false);

  if (total === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">Aucune prestation sélectionnée pour le moment.</p>
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Gérer les prestations
          </button>
        )}
      </div>
    );
  }

  const categoryEntries = Object.entries(groups);
  const showCollapse = total > PREVIEW_CHIP_LIMIT && !expanded;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Prestations attribuées</h3>
          <p className="text-sm text-gray-500">
            {total} prestation{total > 1 ? 's' : ''} attribuée{total > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {(showCollapse ? categoryEntries.slice(0, 2) : categoryEntries).map(([category, items]) => (
          <div key={category} className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">{category}</p>
            <ul className="space-y-1">
              {(showCollapse ? items.slice(0, 4) : items).map((p) => (
                <li key={p.id} className="text-sm text-gray-800">
                  <span className="text-gray-400">•</span> {p.nom}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {total > PREVIEW_CHIP_LIMIT && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm font-medium text-primary-600 hover:underline"
        >
          {expanded ? 'Réduire' : `Voir les ${total} prestations`}
        </button>
      )}

      {onManage && (
        <button
          type="button"
          onClick={onManage}
          className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100"
        >
          Gérer les prestations
        </button>
      )}
    </div>
  );
}

export function buildMemberStatusLine(member) {
  const parts = [];
  const isGerant = member?.protections?.isGerant || member?.roleInterne === 'GERANT';

  if (isGerant) {
    parts.push('Gérante');
  } else if (member?.hasAdminAccess && member?.roleInterne) {
    parts.push(ROLE_LABELS[member.roleInterne] || 'Accès Clickorico');
  }

  if (member?.isIntervenant) {
    const count = member.prestationCount || 0;
    if (count > 0) {
      parts.push(`${PRESTATION_CAPABILITY_LABEL} · ${count} prestation${count > 1 ? 's' : ''}`);
    } else {
      parts.push(PRESTATION_CAPABILITY_LABEL);
    }
  } else {
    parts.push(NO_PRESTATION_CAPABILITY_LABEL);
  }

  return parts.join(' · ');
}

/** Résumé court pour la liste Gestion (sans ligne « Vendeur · … »). */
export function buildMemberCompactPrestationSummary(member) {
  if (!member?.isIntervenant) return 'Ne réalise pas de prestations';
  const count = member.prestationCount || 0;
  if (count === 0) return 'Réalise des prestations';
  return count === 1 ? '1 prestation' : `${count} prestations`;
}

export function buildMemberAccessLine(member) {
  const isGerant = member?.protections?.isGerant || member?.roleInterne === 'GERANT';
  if (!member?.hasAdminAccess && !isGerant) {
    return 'Aucun accès Clickorico';
  }
  return null;
}

export function computeTeamStats(members, absencesToday = 0) {
  const list = members || [];
  return {
    total: list.length,
    prestataires: list.filter((m) => m.isIntervenant).length,
    avecAcces: list.filter((m) => m.hasAdminAccess).length,
    absencesToday,
  };
}

export function TeamSummaryCards({ members, absencesToday = 0 }) {
  const stats = computeTeamStats(members, absencesToday);
  const cards = [
    { label: 'Membres de l\'équipe', value: stats.total, hint: 'Personnes enregistrées' },
    { label: 'Réalisent des prestations', value: stats.prestataires, hint: 'Capacité opérationnelle' },
    { label: 'Accès Clickorico', value: stats.avecAcces, hint: 'Manager, vendeur, caissier' },
    { label: 'Absences aujourd\'hui', value: stats.absencesToday, hint: 'Indisponibilités du jour' },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
        >
          <p className="text-xs font-medium text-gray-500">{card.label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{card.value}</p>
          <p className="mt-0.5 text-xs text-gray-400">{card.hint}</p>
        </div>
      ))}
    </div>
  );
}

export function MemberListRow({ member, onClick, selected = false, compact = false, dense = false }) {
  const fonction = member?.fonctionMetier?.trim();
  const statusLine = buildMemberStatusLine(member);
  const compactSummary = buildMemberCompactPrestationSummary(member);
  const accessLine = buildMemberAccessLine(member);
  const statusLabel = member.actif === false ? 'Inactif' : 'Actif';
  const isActiveStatus = member.actif !== false;

  if (compact || dense) {
    const minH = dense ? 'min-h-[52px]' : 'min-h-[58px]';
    return (
      <button
        type="button"
        onClick={onClick}
        className={`flex ${minH} w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition ${
          selected
            ? 'border-primary-300 bg-primary-50/50 ring-1 ring-primary-200'
            : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50/80'
        }`}
      >
        <MemberAvatar
          member={member}
          size="sm"
          className={`${dense ? 'h-7 w-7' : 'h-8 w-8'} shrink-0 text-xs`}
        />
        <div className="min-w-0 flex-1 leading-tight">
          <div className={`truncate font-semibold text-gray-900 ${dense ? 'text-xs' : 'text-sm'}`}>
            {member.prenom} {member.nom}
          </div>
          {fonction && (
            <div className={`truncate text-gray-600 ${dense ? 'text-[10px]' : 'text-[11px]'}`}>
              {fonction}
            </div>
          )}
          <div className={`truncate text-gray-500 ${dense ? 'text-[9px]' : 'text-[10px]'}`}>
            {compactSummary}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-1.5 py-0.5 font-medium ${
            dense ? 'text-[9px]' : 'text-[10px]'
          } ${isActiveStatus ? 'bg-green-50 text-green-700' : 'bg-rose-100 text-rose-800'}`}
        >
          {statusLabel}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border p-4 text-left shadow-sm transition ${
        selected
          ? 'border-primary-300 bg-primary-50/40 ring-1 ring-primary-200'
          : 'border-gray-200 bg-white hover:border-primary-200 hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-3">
        <MemberAvatar member={member} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900">
            {member.prenom} {member.nom}
          </div>
          {fonction && <div className="mt-0.5 text-sm text-gray-600">{fonction}</div>}
          {statusLine && <div className="mt-1 text-xs leading-relaxed text-gray-500">{statusLine}</div>}
          {accessLine && <div className="text-xs text-gray-500">{accessLine}</div>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              isActiveStatus ? 'bg-green-50 text-green-700' : 'bg-rose-100 text-rose-800'
            }`}
          >
            {statusLabel}
          </span>
          <ChevronRight className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    </button>
  );
}

export const ADMIN_ROLE_OPTIONS = [
  { value: 'MANAGER', label: 'Manager' },
  { value: 'VENDEUR', label: 'Vendeur' },
  { value: 'CAISSIER', label: 'Caissier' },
];

export function IntervenantToggle({ value, onChange, disabled, canManage = true }) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">{PRESTATION_CAPABILITY_LABEL}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          Permet d&apos;attribuer des prestations, un planning et des absences. Ce n&apos;est pas un rôle
          de connexion.
        </p>
      </div>
      <div className="flex gap-3">
        {[true, false].map((opt) => (
          <button
            key={String(opt)}
            type="button"
            disabled={disabled || !canManage}
            onClick={() => onChange(opt)}
            className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition disabled:opacity-50 ${
              value === opt
                ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-sm'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {opt ? 'Oui' : 'Non'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AdminAccessSection({
  member,
  form,
  onChange,
  onSave,
  saving,
  canManage = true,
}) {
  const isGerant = member?.protections?.isGerant || member?.roleInterne === 'GERANT';

  if (isGerant) {
    return (
      <div className="space-y-3 rounded-xl border border-violet-100 bg-violet-50/50 p-4">
        <p className="text-sm font-semibold text-gray-900">Accès Clickorico</p>
        <div className="rounded-lg bg-white px-4 py-3">
          <p className="text-sm font-bold text-violet-800">Gérante</p>
          <p className="mt-1 text-xs text-gray-600">Compte principal de l&apos;établissement</p>
          <p className="mt-2 text-xs text-gray-500">Verrouillé — ce compte ne peut pas être retiré.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-semibold text-gray-900">Accès Clickorico</p>
        <p className="mt-0.5 text-xs text-gray-500">
          Option secondaire — la plupart des personnes n&apos;ont pas besoin de se connecter.
        </p>
      </div>

      <div>
        <p className="mb-2 text-sm text-gray-700">
          Cette personne peut-elle se connecter à l&apos;administration Clickorico ?
        </p>
        <div className="flex gap-3">
          {[false, true].map((opt) => (
            <button
              key={String(opt)}
              type="button"
              disabled={!canManage}
              onClick={() => onChange({ ...form, hasAdminAccess: opt })}
              className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition disabled:opacity-50 ${
                form.hasAdminAccess === opt
                  ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {opt ? 'Oui' : 'Non'}
            </button>
          ))}
        </div>
      </div>

      {form.hasAdminAccess && (
        <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Email de connexion *</label>
            <input
              type="email"
              required
              value={form.adminEmail || ''}
              onChange={(e) => onChange({ ...form, adminEmail: e.target.value })}
              disabled={!canManage}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Niveau d&apos;accès</label>
            <select
              value={form.roleInterne || 'VENDEUR'}
              onChange={(e) => onChange({ ...form, roleInterne: e.target.value })}
              disabled={!canManage}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm disabled:bg-gray-100"
            >
              {ADMIN_ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Rôles fixes Clickorico — aucun rôle personnalisé.</p>
          </div>
        </div>
      )}

      {!canManage && (
        <p className="text-xs text-amber-700">
          Seule la gérante peut modifier les accès Clickorico de l&apos;équipe.
        </p>
      )}

      {canManage && onSave && (
        <button
          type="button"
          onClick={onSave}
          disabled={saving || (form.hasAdminAccess && !form.adminEmail?.trim())}
          className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : "Enregistrer l'accès"}
        </button>
      )}
    </div>
  );
}

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const timeFmt = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

export function formatAbsenceRange(dateDebut, dateFin, motif, type) {
  const start = new Date(dateDebut);
  const end = new Date(dateFin);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { title: type || motif || 'Absence', detail: '' };
  }

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const startTime = timeFmt.format(start);
  const endTime = timeFmt.format(end);
  const isFullDay =
    sameDay && startTime === '00:00' && (endTime === '23:59' || endTime === '00:00');
  const spansDays = !sameDay;

  let title = type || motif || 'Absence';
  if (/cong/i.test(motif || '') || /cong/i.test(type || '')) title = 'Congés';
  else if (!type && spansDays) title = 'Congés';
  else if (!type && sameDay && !isFullDay) title = 'Indisponibilité';

  let detail;
  if (spansDays) {
    detail = `${dateFmt.format(start)} → ${dateFmt.format(end)}`;
  } else if (isFullDay) {
    detail = dateFmt.format(start);
  } else {
    detail = `${dateFmt.format(start)}\n${startTime} → ${endTime}`;
  }

  return { title, detail, motif: motif && title !== motif ? motif : null };
}

export function PlanningDayCard({ label, plages, unavailableLabel = 'Indisponible' }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3 py-3">
      <div className="text-sm font-semibold text-gray-900">{label}</div>
      {plages.length === 0 ? (
        <p className="mt-1 text-sm text-gray-500">{unavailableLabel}</p>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {plages.map((plage, idx) => (
            <li key={plage.id || idx} className="text-sm text-gray-700">
              {plage.heureDebut} → {plage.heureFin}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function UpcomingAbsencesCard({ absences, onAdd }) {
  if (!absences?.length) {
    return (
      <SectionCard title="Absences à venir">
        <p className="text-sm text-gray-500">Aucune absence planifiée.</p>
        {onAdd && (
          <button type="button" onClick={onAdd} className="mt-3 text-sm font-medium text-primary-600 hover:underline">
            + Ajouter une absence
          </button>
        )}
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Absences à venir">
      <ul className="space-y-3">
        {absences.slice(0, 4).map((a) => {
          const { title, detail } = formatAbsenceRange(a.dateDebut, a.dateFin, a.motif, a.type);
          return (
            <li key={a.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
              <p className="text-sm font-medium text-gray-900">{title}</p>
              <p className="text-xs text-gray-600">{detail}</p>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
